import type Store from "@ember-data/store";
import type ApplicationInstance from "@ember/application/instance";
import { getOwner } from "@ember/owner";
import dayjs from "dayjs";
import type { EmberChangeset } from "ember-changeset";
import { Changeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { BufferedChangeset } from "ember-changeset/types";
import type IntlService from "ember-intl/services/intl";
import evValidateNumber from "ember-validators/number";
import BloodPressureMeasuredJournalEvent from "parog-web/models/blood-pressure-measured-journal-event";
import type BloodPressureSessionStartedJournalEvent from "parog-web/models/blood-pressure-session-started-journal-event";
import type Patient from "parog-web/models/patient";
import type FetcherService from "parog-web/services/fetcher";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
import { sortBy } from "parog-web/utils/sort";

export interface DayMeasurement<
  T extends BloodPressureMeasuredJournalEvent | EmberChangeset
> {
  morning: T[];
  evening: T[];
}

const measurementValidations = {
  systolicPressure: [
    validatePresence({
      presence: true,
      description: "La pression systolique"
    }),
    (_key, value) => {
      value = parseInt(value);
      const result =
        evValidateNumber(value, { integer: true, gt: 0, lt: 25 }) === true ||
        evValidateNumber(value, { integer: true, gt: 50, lt: 250 }) === true;
      return result === true
        ? true
        : "La pression systolique est en dehors des seuils, vérifiez la valeur";
    }
  ],
  diastolicPressure: [
    validatePresence({
      presence: true,
      description: "La pression diastolique"
    }),
    (_key, value) => {
      value = parseInt(value);
      const result =
        evValidateNumber(value, { integer: true, gt: 0, lt: 15 }) === true ||
        evValidateNumber(value, { integer: true, gt: 35, lt: 150 }) === true;
      return result === true
        ? true
        : "La pression diastolique est en dehors des seuils, vérifiez la valeur";
    }
  ],
  heartRate: validateNumber({
    integer: true,
    gt: 0,
    description: "BPM",
    allowBlank: true
  })
};

/**
 * Validate that a date does not overlap an existing session
 * @param sessions
 * @returns
 */
const validateOverlapping =
  (sessions: BloodPressureSessionStartedJournalEvent[], intl: IntlService) =>
  (_key, newValue, _oldValue, _changes, content) => {
    const overlappedSession = sessions.find(
      session =>
        content != session && session.isSessionStartDateMayOverlap(newValue)
    );

    return (
      overlappedSession === undefined ||
      `La date de début de session chevauche la session existante du ${intl.formatDate(
        overlappedSession.occurredAt
      )}`
    );
  };

/**
 * The goal of this class is to generate a model that will be used by the BloodPressure::SessionMeasurement view.
 *
 * It needs a BloodPressureSession started and create the missing BloodPressureMeasured for each day
 * (we want at least 3 measurement for the morning, 3 for the evening and for 3 days)
 * Then the measurement are encapsulated inside a changeset ready to use.
 * Finally, the new measurements that have not changed are removed from the store.
 */
export default class BloodPressureSessionModelView {
  dayMeasurements: DayMeasurement<BloodPressureMeasuredJournalEvent>[];
  dayMeasurementChangesets: DayMeasurement<EmberChangeset>[];
  sessionChangeset: EmberChangeset;

  get bloodPressureSession(): BloodPressureSessionStartedJournalEvent {
    return this.session;
  }

  get currentSession(): BloodPressureSessionStartedJournalEvent {
    return this.session;
  }

  get isDirty(): boolean {
    return (
      this.sessionChangeset.isDirty ||
      this.allChangesets.some(changeset => this.isChangesetDirty(changeset))
    );
  }

  get hasWithingsMeasurement(): boolean {
    return this.allChangesets.some(m => m.source === "withings");
  }

  get hasMedicMeasurement(): boolean {
    return this.allChangesets.some(
      m => (m.creator as { isMedic?: boolean })?.isMedic
    );
  }

  private get sessionValidations(): { occurredAt: unknown[] } {
    return {
      occurredAt: [
        validateDate({
          message: "La date de début de session ne doit pas être dans le futur",
          onOrBefore: dayjs().endOf("day").toDate()
        }),
        validateOverlapping(
          this.patient.bloodPressureSessionStartedJournalEvents,
          this.intl
        )
      ]
    };
  }

  private get intl(): IntlService {
    return (getOwner(this.store) as ApplicationInstance).lookup("service:intl");
  }

  constructor(
    private store: Store,
    private fetcher: FetcherService,
    private session: BloodPressureSessionStartedJournalEvent,
    private patient: Patient
  ) {
    this.sessionChangeset = Changeset(
      this.session,
      lookupValidator(this.sessionValidations),
      this.sessionValidations
    );
    this.dayMeasurements = this.createMissingMeasurements();
    this.dayMeasurementChangesets = this.dayMeasurements.map(day => {
      return {
        morning: day.morning.map(m =>
          Changeset(
            m,
            lookupValidator(measurementValidations),
            measurementValidations
          )
        ),
        evening: day.evening.map(m =>
          Changeset(
            m,
            lookupValidator(measurementValidations),
            measurementValidations
          )
        )
      };
    });
  }

  async validate(): Promise<boolean> {
    const changesets = this.allUsedChangesets;
    await Promise.all([
      ...changesets.map(changeset => changeset.validate()),
      this.sessionChangeset.validate()
    ]);
    return [...changesets, this.sessionChangeset].every(
      changeset => changeset.isValid
    );
  }

  async deleteSession(): Promise<void> {
    if (this.session.get("isNew")) return;

    await Promise.all(
      this.session.bloodPressureMeasuredJournalEvents.slice().map(m => {
        if (!m.get("isNew")) {
          return m.archive();
        }
        return m.deleteRecord();
      })
    );

    await this.session.archive();
  }

  /**
   * Save every changeset
   * The method will not check if the changesets are valid or not, you need to call 'validate' first
   */
  async save(): Promise<void> {
    /**
     * If the session is new it is possible to modify its "occurredAt".
     * So we have to ensure that the measurements correspond to the session date.
     */
    if (this.session.get("isNew")) {
      this.shiftMeasurementDayToFitSession();
    }

    const changesets = this.allChangesets.sort(sortBy("occurredAt"));

    /**
     * We can't parallelize the saving because it is important to save
     * the older measure before the new ones in cases this is a new session.
     * In fact if for example the measure of day 2 is saved before the day 1
     * then the session is creating from day two and the day one (which will
     * arrvied on the back after) will create a second session
     */
    for (const changeset of changesets) {
      if (this.isChangesetDirty(changeset)) {
        await this.saveChangeset(changeset);
      } else {
        // Otherwise remove the jes that have not been used
        const je = changeset.data as BloodPressureMeasuredJournalEvent;
        if (je.get("isNew")) {
          je.deleteRecord();
        }
      }
    }

    // Then remove the session if it is new and retrieve the one created by the back
    if (this.session.get("isNew")) {
      this.session.deleteRecord();
      await this.fetcher.loadRecords(
        "blood-pressure-session-started-journal-event",
        {
          filter: {
            created_after: formatToISO8601Date(
              dayjs().subtract(1, "day").toDate()
            ),
            patient_id: this.patient.id
          },
          reload: true
        }
      );
    }
  }

  /**
   * This method ensures that the dates of the measurements are in agreement with the date of the session
   */
  private shiftMeasurementDayToFitSession(): void {
    // Do not modify the date if the session already exists
    if (!this.session.get("isNew")) return;

    // Update every measurement's occurredAt accordingly.
    this.dayMeasurementChangesets.forEach((dayMeasurements, sessionDay) => {
      const computeDate = (measurement, index, isMorning): void => {
        const occurredAt =
          BloodPressureMeasuredJournalEvent.createMeasurementDate(
            this.sessionChangeset.get("occurredAt"),
            sessionDay,
            isMorning,
            index
          );
        measurement.set("occurredAt", occurredAt);
      };

      dayMeasurements.morning.forEach((m, i) => computeDate(m, i, true));
      dayMeasurements.evening.forEach((m, i) => computeDate(m, i, false));
    });
  }

  private saveChangeset(
    changeset: EmberChangeset
  ): Promise<BloodPressureMeasuredJournalEvent> {
    changeset.set("patient", this.patient);
    changeset.execute();
    return (changeset.data as BloodPressureMeasuredJournalEvent).save();
  }

  /**
   * Every changeset that have been used
   */
  private get allUsedChangesets(): BufferedChangeset[] {
    return this.allChangesets.filter(changeset =>
      this.isChangesetDirty(changeset)
    );
  }

  /**
   * It is possible to have the date that has changed without the object being really dirty
   * because changing the date of the session will change the date of the measurements
   * So we want to validate only the changesets that have been used
   * @param changeset
   * @returns
   */
  private isChangesetDirty(changeset: BufferedChangeset): boolean {
    return (
      changeset.isDirty &&
      !(
        changeset.changes.length === 1 &&
        changeset.changes[0]?.key === "occurredAt"
      )
    );
  }

  /**
   * Every mesurement changeset flattened
   */
  private get allChangesets(): EmberChangeset[] {
    return this.dayMeasurementChangesets.reduce(
      (changesets, dayMeasurement) => [
        ...changesets,
        ...dayMeasurement.morning,
        ...dayMeasurement.evening
      ],
      []
    );
  }

  /**
   * A session should be composed of 3 measures for morning and 3 for evening during 3 days.
   * This methods will return new BloodPressureMeasuredJournalEvent for each missing measurements
   * The method will format the measurement in a DayMeasurements interface
   */
  private createMissingMeasurements(): DayMeasurement<BloodPressureMeasuredJournalEvent>[] {
    const measurements: DayMeasurement<BloodPressureMeasuredJournalEvent>[] =
      [];

    for (let i = 0; i < 3; i++) {
      const morning = this.session.getMorningMeasurementsOfSessionDay(i);
      const evening = this.session.getEveningMeasurementsOfSessionDay(i);

      measurements[i] = {
        morning: this.completedArrayWithMissingMeasurements({
          measures: morning,
          sessionDay: i,
          isMorning: true
        }),
        evening: this.completedArrayWithMissingMeasurements({
          measures: evening,
          sessionDay: i,
          isMorning: false
        })
      };
    }

    return measurements;
  }

  /**
   * Return a copy of the given array that add every missing measurements
   */
  private completedArrayWithMissingMeasurements({
    measures,
    sessionDay,
    isMorning
  }: {
    measures: BloodPressureMeasuredJournalEvent[];
    sessionDay: number;
    isMorning: boolean;
  }): BloodPressureMeasuredJournalEvent[] {
    const everyMeasurements: BloodPressureMeasuredJournalEvent[] = [
      ...measures
    ];

    // Each array should have 3 measurements
    while (everyMeasurements.length < 3) {
      const occurredAt = this.session.computeDateForMeasurement(
        sessionDay,
        isMorning
      );

      // The patient is not set on the created record until we save the model.
      // Like this we avoid to have wrong records associated to the patient in the store
      everyMeasurements.push(
        this.store.createRecord("blood-pressure-measured-journal-event", {
          occurredAt
        })
      );
    }
    return everyMeasurements;
  }
}
